/**
 * S23 — Object Explorer "Dashboards" root node builder.
 *
 * Pure functions only (no React, no store reads) so they're cheap to unit
 * test — see `dashboardExplorerNode.test.ts`. The root node is OMITTED
 * (returns `null`) whenever there are zero dashboards: "zero footprint for
 * non-users" (plan directive). The caller (`DatabaseExplorer.tsx`) is
 * responsible for inserting/removing/updating the node in `treeData`.
 *
 * Identity rule (Docs/ObjectExplorer.md "preloader must never clobber live
 * tree state"): a child node's row only ever renders `dashboard.name`, so an
 * update to any OTHER field (tiles, revision, updatedAt, …) must reuse the
 * exact same node object — only a name change may produce a new one.
 */
import type {
  DashboardsRootTreeNode,
  DashboardTreeNode,
} from '../../../explorer/models/nodeTypes';
import { createNodeId } from '../../../explorer/utils/nodeIdUtils';
import type { Dashboard } from '../types/dashboardTypes';

export const DASHBOARDS_ROOT_NODE_ID = 'dashboards-root';

export function createDashboardNodeId(dashboardId: string): string {
  return createNodeId('dashboard', dashboardId);
}

/**
 * Deterministic order regardless of the store's array order — ties broken
 * by id so re-sorting an unchanged set never reshuffles same-name siblings.
 */
function sortDashboards(dashboards: Dashboard[]): Dashboard[] {
  return [...dashboards].sort(
    (a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id),
  );
}

function buildDashboardChildNode(
  dashboard: Dashboard,
  existing: DashboardTreeNode | undefined,
): DashboardTreeNode {
  if (existing && existing.name === dashboard.name) {
    // Nothing rendering-relevant changed — reuse the same object so React
    // doesn't remount the row (and any collapsed/focus state on it survives).
    return existing;
  }
  return {
    id: createDashboardNodeId(dashboard.id),
    parentId: DASHBOARDS_ROOT_NODE_ID,
    name: dashboard.name,
    type: 'dashboard',
    metadata: { dashboardId: dashboard.id },
  };
}

/**
 * Builds the "Dashboards" root node from the current dashboard list, or
 * `null` when there are none (the caller removes the node from the tree).
 * Pass the tree's current root (if any) so unchanged children keep their
 * object identity and the root's expanded state survives the rebuild.
 */
export function buildDashboardsRootNode(
  dashboards: Dashboard[],
  existingRoot?: DashboardsRootTreeNode | null,
): DashboardsRootTreeNode | null {
  if (dashboards.length === 0) return null;

  const existingById = new Map(
    ((existingRoot?.children as DashboardTreeNode[] | undefined) ?? []).map(
      (c) => [c.metadata.dashboardId, c] as const,
    ),
  );

  const children = sortDashboards(dashboards).map((d) =>
    buildDashboardChildNode(d, existingById.get(d.id)),
  );

  return {
    id: DASHBOARDS_ROOT_NODE_ID,
    parentId: null,
    name: 'Dashboards',
    type: 'dashboards-root',
    isExpanded: existingRoot?.isExpanded ?? true,
    children,
    metadata: { count: dashboards.length },
  };
}
